/**
 * SettingsIntegrations — Card-based layout
 *
 * Card layout (ported from EBTimeEntry IntegrationsPage Phase 2):
 *  - Each card shows company name, code, user count, integration status pills, overall health chip
 *  - Active/inactive dimming inline
 *  - Sheet opens when a card is clicked
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Building2,
  Plus,
  Search,
  PoundSterling,
  Clock,
  Receipt,
  CalendarDays,
  Users,
  ChevronRight,
  ShoppingCart,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Circle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getCompanies } from '@/services/integrationsRedesignService';
import type {
  CompanyWithIntegrations,
  SimpleIntegrationConfig,
  IntegrationConnectionStatus,
} from '@/types/integrations-redesign';
import {
  FINANCE_PRODUCT_LABELS,
  TIMESHEET_PRODUCT_LABELS,
  EXPENSE_PRODUCT_LABELS,
  PLANNER_PRODUCT_LABELS,
  PURCHASING_PRODUCT_LABELS,
} from '@/types/integrations-redesign';
import { CompanySheet } from '@/components/integrations/CompanySheet';
import { useDataMode } from '@/contexts/DataModeContext';
import { useCompany } from '@/contexts/CompanyContext';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getProductLabel(
  config: SimpleIntegrationConfig | undefined | null,
  labels: Record<string, string>,
): string | null {
  if (!config || !config.product) return null;
  return labels[config.product] ?? config.product;
}

function getCompanyHealth(
  company: CompanyWithIntegrations,
): 'all-connected' | 'has-errors' | 'not-connected' | 'not-configured' {
  const statuses: IntegrationConnectionStatus[] = [
    company.financeIntegration.status,
    company.timesheetIntegration.status,
    company.expenseIntegration.status,
    company.plannerIntegration.status,
    company.purchasingIntegration.status,
  ];
  const configured = statuses.filter((s) => s !== 'not_configured');
  if (configured.length === 0) return 'not-configured';
  if (configured.some((s) => s === 'error')) return 'has-errors';
  if (configured.some((s) => s === 'not_connected')) return 'not-connected';
  return 'all-connected';
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: IntegrationConnectionStatus }) {
  if (status === 'connected')
    return <span className="h-2 w-2 rounded-full bg-success-500 flex-shrink-0 ring-2 ring-success-500/20" />;
  if (status === 'error')
    return <span className="h-2 w-2 rounded-full bg-destructive flex-shrink-0 ring-2 ring-destructive/20" />;
  if (status === 'not_connected')
    return <span className="h-2 w-2 rounded-full bg-warning-500 flex-shrink-0 ring-2 ring-warning-500/20" />;
  return <span className="h-2 w-2 rounded-full bg-muted-foreground/30 flex-shrink-0 ring-2 ring-muted/40" />;
}

// ---------------------------------------------------------------------------
// Integration pill — icon + status dot + product name (or "not set" hint)
// ---------------------------------------------------------------------------

function IntegrationPill({
  icon,
  label,
  config,
  labels,
}: {
  icon: React.ReactNode;
  label: string;
  config: SimpleIntegrationConfig | undefined | null;
  labels: Record<string, string>;
}) {
  const productName = getProductLabel(config, labels);
  const status = config?.status ?? 'not_configured';

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-muted-foreground/60 flex-shrink-0">{icon}</span>
      {productName ? (
        <>
          <StatusDot status={status} />
          <span className="text-xs font-medium truncate">{productName}</span>
        </>
      ) : (
        <span className="text-xs text-muted-foreground/45 italic">{label} not set</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Health indicator pill
// ---------------------------------------------------------------------------

function HealthIndicator({ health }: { health: ReturnType<typeof getCompanyHealth> }) {
  switch (health) {
    case 'all-connected':
      return (
        <div className="flex items-center gap-1 text-success-700 bg-success-50 border border-success-200 rounded-full px-2 py-0.5">
          <CheckCircle2 className="h-3 w-3" />
          <span className="text-[10px] font-semibold">All Connected</span>
        </div>
      );
    case 'has-errors':
      return (
        <div className="flex items-center gap-1 text-destructive bg-destructive/5 border border-destructive/20 rounded-full px-2 py-0.5">
          <XCircle className="h-3 w-3" />
          <span className="text-[10px] font-semibold">Has Errors</span>
        </div>
      );
    case 'not-connected':
      return (
        <div className="flex items-center gap-1 text-warning-700 bg-warning-50 border border-warning-200 rounded-full px-2 py-0.5">
          <AlertCircle className="h-3 w-3" />
          <span className="text-[10px] font-semibold">Not Connected</span>
        </div>
      );
    case 'not-configured':
      return (
        <div className="flex items-center gap-1 text-muted-foreground bg-muted/60 border rounded-full px-2 py-0.5">
          <Circle className="h-3 w-3" />
          <span className="text-[10px] font-semibold">Not Configured</span>
        </div>
      );
  }
}

// ---------------------------------------------------------------------------
// Company card — full-width list style matching EBTimeEntry mockup
// ---------------------------------------------------------------------------

interface CompanyCardProps {
  company: CompanyWithIntegrations;
  isSelected: boolean;
  onClick: () => void;
}

function CompanyCard({ company, isSelected, onClick }: CompanyCardProps) {
  const health = getCompanyHealth(company);
  const isInactive = company.status === 'inactive';
  const initials = company.code.slice(0, 2).toUpperCase();

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-lg border bg-card p-4 transition-all duration-150 hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
        isSelected && 'border-primary/60 bg-primary/5 shadow-sm',
        isInactive && 'opacity-60',
      )}
    >
      {/* Top row: avatar + identity + health + chevron */}
      <div className="flex items-start gap-3 min-w-0">
        {/* Code-initial avatar */}
        <div
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md flex-shrink-0 text-sm font-bold',
            isInactive ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary',
          )}
        >
          {initials}
        </div>

        {/* Name + code + user count */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm leading-tight">{company.name}</span>
            {isInactive && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                Inactive
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-muted-foreground font-mono">{company.code}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              {company.userCount} {company.userCount === 1 ? 'user' : 'users'}
            </span>
          </div>
        </div>

        {/* Health + chevron */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <HealthIndicator health={health} />
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-150',
              isSelected && 'rotate-90 text-primary',
            )}
          />
        </div>
      </div>

      {/* Integration pills: 3-col top row + 2-col bottom row, indented under avatar */}
      <div className="grid grid-cols-3 gap-x-6 gap-y-1.5 mt-3 pl-12">
        <IntegrationPill
          icon={<PoundSterling className="h-3 w-3" />}
          label="Financial"
          config={company.financeIntegration}
          labels={FINANCE_PRODUCT_LABELS}
        />
        <IntegrationPill
          icon={<Clock className="h-3 w-3" />}
          label="Timesheets"
          config={company.timesheetIntegration}
          labels={TIMESHEET_PRODUCT_LABELS}
        />
        <IntegrationPill
          icon={<Receipt className="h-3 w-3" />}
          label="Expense"
          config={company.expenseIntegration}
          labels={EXPENSE_PRODUCT_LABELS}
        />
        <IntegrationPill
          icon={<CalendarDays className="h-3 w-3" />}
          label="Scheduler"
          config={company.plannerIntegration}
          labels={PLANNER_PRODUCT_LABELS}
        />
        <IntegrationPill
          icon={<ShoppingCart className="h-3 w-3" />}
          label="Purchasing"
          config={company.purchasingIntegration}
          labels={PURCHASING_PRODUCT_LABELS}
        />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Card loading skeleton
// ---------------------------------------------------------------------------

function CardSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start gap-3">
        <Skeleton className="h-9 w-9 rounded-md flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-5 w-28 rounded-full" />
      </div>
      <div className="grid grid-cols-3 gap-x-6 gap-y-1.5 pl-12">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-3.5 w-32 rounded" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function SettingsIntegrations() {
  const [companies, setCompanies] = useState<CompanyWithIntegrations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithIntegrations | null>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const { mode } = useDataMode();
  const { refreshCompanies } = useCompany();

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const data = await getCompanies();
      const sorted = [...data].sort((a, b) => {
        if (a.status === b.status) return a.name.localeCompare(b.name);
        return a.status === 'active' ? -1 : 1;
      });
      setCompanies(sorted);
    } catch {
      // toast error if toast is available
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, [mode]);

  // -------------------------------------------------------------------------
  // Filtered list
  // -------------------------------------------------------------------------

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return companies.filter(
      (c) =>
        (c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)) &&
        (statusFilter === 'all' || c.status === statusFilter),
    );
  }, [companies, searchQuery, statusFilter]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Add Company action */}
      <div className="flex justify-end">
        <Button onClick={() => setShowCreateSheet(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Search & filter row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary bar */}
      {!isLoading && companies.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>
            {filtered.length} of {companies.length} companies
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-success-500 inline-block" />
            {companies.filter((c) => getCompanyHealth(c) === 'all-connected').length} fully
            connected
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-destructive inline-block" />
            {companies.filter((c) => getCompanyHealth(c) === 'has-errors').length} with errors
          </span>
        </div>
      )}

      {/* Card list */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour="settings-integrations-list">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <Building2 className="h-12 w-12 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">No companies found</p>
          {searchQuery && (
            <button
              className="text-xs text-primary underline"
              onClick={() => setSearchQuery('')}
            >
              Clear search
            </button>
          )}
          {!searchQuery && (
            <Button size="sm" onClick={() => setShowCreateSheet(true)}>
              <Plus className="mr-2 h-3.5 w-3.5" /> Add Company
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour="settings-integrations-mapping">
          {filtered.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              isSelected={selectedCompany?.id === company.id}
              onClick={() => setSelectedCompany(company)}
            />
          ))}
        </div>
      )}

      {/* View / edit sheet — opens when a card is clicked */}
      <CompanySheet
        company={selectedCompany}
        open={!!selectedCompany}
        onOpenChange={(open) => {
          if (!open) setSelectedCompany(null);
        }}
        onCompanyUpdated={(updated) => {
          setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
          setSelectedCompany(updated);
          refreshCompanies();
        }}
        onCompanyCreated={async () => {
          setShowCreateSheet(false);
          await loadCompanies();
        }}
        existingCodes={companies.map((c) => c.code)}
      />

      {/* Create sheet — opens from the Add Company button */}
      <CompanySheet
        company={null}
        open={showCreateSheet}
        onOpenChange={setShowCreateSheet}
        onCompanyUpdated={() => {}}
        onCompanyCreated={async (created) => {
          setShowCreateSheet(false);
          await loadCompanies();
          setSelectedCompany(created);
        }}
        existingCodes={companies.map((c) => c.code)}
      />
    </div>
  );
}

export default SettingsIntegrations;
